import React, { forwardRef } from 'react';
import { Textarea as FiverTextarea, FormHelperText } from '@fiverr-private/forms';
import { Container } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';

interface FormTextAreaProps {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;

  valid?: boolean;
  dirty?: boolean;
  pending?: boolean;
  reason?: string;
  errorMessage?: string;
  successMessage?: string;
  hint?: string;
  tip?: string;

  maxLength?: number;
  rows?: number;
  cols?: number;

  isRequired?: boolean;

  touched?: boolean;
  isEmpty?: () => boolean;
  setValue?: (value: string) => void;
  children?: React.ReactNode;
}

export const FormTextArea = forwardRef<HTMLTextAreaElement, FormTextAreaProps>(
  (
    {
      value,
      defaultValue,
      placeholder,
      disabled = false,
      onChange,
      onFocus,
      onBlur,
      valid = true,
      dirty = false,
      pending = false,
      reason,
      errorMessage,
      successMessage,
      hint,
      tip,

      maxLength,
      rows = 3,
      cols,

      isRequired = false,

      touched: _touched,
      isEmpty: _isEmpty,
      setValue: _setValue,

      children: _children,
      ...rest
    },
    ref
  ) => {
    const hasError = dirty && !pending && !valid;
    const hasSuccess = dirty && !pending && valid && !hasError;

    const displayErrorMessage = hasError ? reason || errorMessage : undefined;
    const displaySuccessMessage = hasSuccess ? successMessage : undefined;
    const displayHint = !hasError && !hasSuccess ? hint || tip : undefined;

    return (
      <Container paddingY="2" position="relative" minHeight="120px">
        {isRequired && (
          <Typography color="red_700" position="absolute" top="4px" right="8px" zIndex="2">
            *
          </Typography>
        )}

        <FiverTextarea
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          isError={hasError}
          isSuccess={hasSuccess}
          maxLength={maxLength}
          rows={rows}
          cols={cols}
          {...rest}
        />

        {(displayErrorMessage || displaySuccessMessage || displayHint) && (
          <Box fontSize="b_xs">
            <FormHelperText
              error={displayErrorMessage}
              success={displaySuccessMessage}
              hint={displayHint}
              isError={hasError}
              isSuccess={hasSuccess}
            />
          </Box>
        )}
      </Container>
    );
  }
);

FormTextArea.displayName = 'FormTextArea';
